import { Injectable, OnDestroy } from '@angular/core';

/**
 * Prevents background page scroll while slider panels / modal overlays are open.
 * Hides the native page scrollbar while locked so it does not paint above slider panels.
 */
@Injectable({
  providedIn: 'root',
})
export class BodyScrollLockService implements OnDestroy {
  private observer?: MutationObserver;
  private syncFrame = 0;
  private isLocked = false;
  private savedScrollY = 0;
  private scrollbarCompensation = 0;

  private readonly lockTriggerSelector = [
    '.backdrop:not(.ai-help-backdrop)',
    '.ai-help-backdrop--visible',
    '.slider-backdrop',
    '.slider-overlay',
    '.ai-hours-log-slider-backdrop',
    '.modal-overlay',
    '.change-password-overlay',
    '.assign_modal_overlay',
    '.approvals_modal_overlay',
    '.time_log_modal_overlay.is-ready',
    '.dialog-overlay',
    '.media-modal-overlay',
    '.member-detail-modal-overlay',
    '.upload-modal-overlay',
    '.image-modal-overlay',
    '.link-modal-overlay',
    '.mobile_radial_menu_overlay.active',
  ].join(',');

  private readonly scrollAllowSelector = [
    '.slide-panel',
    '.slider-panel',
    '.modal-container',
    /** Timesheet Add Comment uses `.modal-content`, not `.modal-container`. */
    '.comment-modal .modal-content',
    '.ai-help-shell',
    '.ng-dropdown-panel',
    '.cdk-overlay-container',
    '.mat-mdc-dialog-container',
    '[data-scroll-lock-allow]',
  ].join(',');

  private readonly scrollKeys = new Set([
    'ArrowUp',
    'ArrowDown',
    'PageUp',
    'PageDown',
    'Home',
    'End',
    ' ',
  ]);

  private readonly onWheel = (event: WheelEvent): void => {
    if (!this.isLocked || this.canScrollInside(event)) {
      return;
    }
    event.preventDefault();
  };

  private readonly onTouchMove = (event: TouchEvent): void => {
    if (!this.isLocked || this.isInsideScrollAllowTarget(event.target)) {
      return;
    }
    event.preventDefault();
  };

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    if (!this.isLocked || !this.scrollKeys.has(event.key)) {
      return;
    }
    // Never block typing / caret keys inside editors (Quill contenteditable, inputs, etc.).
    if (this.isEditableTarget(event.target) || this.isInsideScrollAllowTarget(event.target)) {
      return;
    }
    event.preventDefault();
  };

  private readonly onScroll = (): void => {
    if (!this.isLocked) {
      return;
    }

    const current = window.scrollY || document.documentElement.scrollTop || 0;
    if (current !== this.savedScrollY) {
      window.scrollTo(0, this.savedScrollY);
    }
  };

  private getScrollbarWidth(): number {
    return Math.max(0, window.innerWidth - document.documentElement.clientWidth);
  }

  startWatching(): void {
    if (this.observer || typeof document === 'undefined') {
      return;
    }

    this.observer = new MutationObserver(() => this.scheduleSync());
    this.observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['class'],
    });

    this.scheduleSync();
  }

  stopWatching(): void {
    if (this.syncFrame) {
      cancelAnimationFrame(this.syncFrame);
      this.syncFrame = 0;
    }

    this.observer?.disconnect();
    this.observer = undefined;
    this.releaseLock();
  }

  ngOnDestroy(): void {
    this.stopWatching();
  }

  private scheduleSync(): void {
    if (this.syncFrame) {
      cancelAnimationFrame(this.syncFrame);
    }

    this.syncFrame = requestAnimationFrame(() => {
      this.syncFrame = 0;
      this.syncFromDom();
    });
  }

  private syncFromDom(): void {
    const shouldLock = this.shouldLock();

    if (shouldLock && !this.isLocked) {
      this.applyLock();
      return;
    }

    if (!shouldLock && this.isLocked) {
      this.releaseLock();
    }
  }

  private shouldLock(): boolean {
    return (
      document.body.classList.contains('modal-open') ||
      !!document.querySelector(this.lockTriggerSelector)
    );
  }

  private applyLock(): void {
    this.savedScrollY = window.scrollY || document.documentElement.scrollTop || 0;
    this.scrollbarCompensation = this.getScrollbarWidth();
    this.isLocked = true;

    document.documentElement.classList.add('body-scroll-locked');
    document.body.classList.add('body-scroll-locked');

    if (this.scrollbarCompensation > 0) {
      document.body.style.paddingRight = `${this.scrollbarCompensation}px`;
    }

    document.addEventListener('wheel', this.onWheel, { passive: false, capture: true });
    document.addEventListener('touchmove', this.onTouchMove, { passive: false, capture: true });
    document.addEventListener('keydown', this.onKeyDown, { capture: true });
    window.addEventListener('scroll', this.onScroll, { passive: true, capture: true });
  }

  private releaseLock(): void {
    this.isLocked = false;

    document.removeEventListener('wheel', this.onWheel, true);
    document.removeEventListener('touchmove', this.onTouchMove, true);
    document.removeEventListener('keydown', this.onKeyDown, true);
    window.removeEventListener('scroll', this.onScroll, true);

    document.documentElement.classList.remove('body-scroll-locked');
    document.body.classList.remove('body-scroll-locked');
    document.body.style.paddingRight = '';
  }

  private isInsideScrollAllowTarget(target: EventTarget | null): boolean {
    return target instanceof Element && !!target.closest(this.scrollAllowSelector);
  }

  /** True when Space/arrows should type or move the caret, not scroll the page. */
  private isEditableTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }
    const editable = target.closest(
      'input, textarea, select, [contenteditable="true"], [contenteditable=""], .ql-editor'
    );
    if (!editable) {
      return false;
    }
    if (editable instanceof HTMLInputElement || editable instanceof HTMLTextAreaElement) {
      return !editable.readOnly && !editable.disabled;
    }
    return true;
  }

  private canScrollInside(event: WheelEvent): boolean {
    if (!this.isInsideScrollAllowTarget(event.target)) {
      return false;
    }

    let node = event.target instanceof Element ? event.target : null;
    while (node && node !== document.body) {
      if (this.isScrollableElement(node)) {
        const atTop = node.scrollTop <= 0;
        const atBottom =
          node.scrollTop + node.clientHeight >= node.scrollHeight - 1;
        const scrollingUp = event.deltaY < 0;
        const scrollingDown = event.deltaY > 0;

        if ((scrollingUp && !atTop) || (scrollingDown && !atBottom)) {
          return true;
        }
      }

      node = node.parentElement;
    }

    return false;
  }

  private isScrollableElement(element: Element): boolean {
    const style = window.getComputedStyle(element);
    const overflowY = style.overflowY;

    if (overflowY !== 'auto' && overflowY !== 'scroll' && overflowY !== 'overlay') {
      return false;
    }

    return element.scrollHeight > element.clientHeight;
  }
}
